import Instrument from './Instrument.js';
import {mate} from 'genetix/Flip.js';
import Notes from 'midi/Notes.js';
import {A_RATE} from 'constants/automation-rates.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import Tone from 'tone';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import {FM_SYNTH} from 'constants/instrument-types.js';
import FMCarrierOscillator from './FMCarrierOscillator.js';
import FMModulatorOscillator from './FMModulatorOscillator.js';
import Effects from 'effects/Effects.js';
import {AMP, MODULATION, FILTER, FILTER_2} from 'constants/envelope-types.js';
import SynthEnvelope from './SynthEnvelope.js';

class FMSynth extends Instrument {
    constructor(sequencerEngine, mixSession, context) {
        super();

        this.notes = [];

        // state objects (mainly for registering parameters)
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.mixSession = mixSession;
        this.context = context.getCopy().forInstrument(this);

        // envelopes: amp, filter, pitch
        this.ampEnvelope = new SynthEnvelope(
            sequencerEngine, mixSession, this.context);

        this.modulationEnvelope = new SynthEnvelope(
            sequencerEngine, mixSession, this.context);

        this.filter = new Effects.FilterEffect(sequencerEngine, A_RATE);
        this.filterEnvelope = new SynthEnvelope(
            sequencerEngine, mixSession, this.context,
            this.filter.effectNode.frequency, 0, 10000);
        this.filterEnvelope2 = new SynthEnvelope(
            sequencerEngine, mixSession, this.context,
            this.filter.effectNode.frequency, 0, 10000);

        this.carrierOscillator = new FMCarrierOscillator(
            sequencerEngine, mixSession, this.context);
        this.modulatorOscillator = new FMModulatorOscillator(
            sequencerEngine, mixSession, this.context, this);
        this.noiseOscillator = new Tone.NoiseSynth();

        this.connectSynthToFilter();

        this.parameters = [];

        this.setupParameters();

        this.carrierOscillator.onNewSynth = () => this.onNewSynth();

        this.lastTime = undefined;
        this.currentNotes = [];
    }

    getSubDevices() {
        return [
            {label: "Filter", value: this},
            {label: "Carrier Oscillator", value: this.carrierOscillator},
            {label: "Modulator Oscillator", value: this.modulatorOscillator},
            {label: "Amp Envelope", value: this.ampEnvelope},
            {label: "Filter Envelope", value: this.filterEnvelope},
            {label: "Filter Envelope 2", value: this.filterEnvelope2},
            {label: "Modulation Envelope", value: this.modulationEnvelope},
        ];
    }

    get synth() {
        return this.carrierOscillator.synth;
    }

    onNewSynth() {
        this.connectSynthToFilter();
        this.modulatorOscillator.refreshParameters();
    }

    triggerAttack(transpose, time, velocity) {
        // refresh the filter envelopes (so that it gets the latest 
        // LFO values)
        this.filterEnvelope.refresh();
        this.filterEnvelope2.refresh();

        let note = this.carrierOscillator.getNoteFromTranspose(transpose);
        this.notes.push(note);

        // trigger filter 
        this.filterEnvelope.triggerAttack(time, velocity);
        this.filterEnvelope2.triggerAttack(time, velocity);

        // trigger 
        this.synth.triggerAttack(note, time, velocity);
        this.noiseOscillator.triggerAttack(time, velocity);
    }

    triggerAttackRelease(transpose, duration, time, velocity) {
        // BUG: for some reason some notes dont ever release :(
        
        let note = this.carrierOscillator.getNoteFromTranspose(transpose);
        this.notes.push(note);
        // refresh the filter envelopes (so that it gets
        // the latest LFO values)
        this.filterEnvelope.refresh();
        this.filterEnvelope2.refresh();

        // trigger filter envelope
        this.filterEnvelope.triggerAttackRelease(duration, time, velocity);
        this.filterEnvelope2.triggerAttackRelease(duration, time, velocity);

        // trigger each oscillator
        this.synth.triggerAttackRelease(
            note, duration, time, velocity);
        this.noiseOscillator.triggerAttackRelease(duration, time, velocity);
    }

    triggerRelease(transposes) {
        let notes = transposes
            .map(transpose=> this.carrierOscillator.getNoteFromTranspose(transpose));
        // trigger filter envelope
        this.filterEnvelope.triggerRelease();
        this.filterEnvelope2.triggerRelease();

        // trigger each oscillator
        this.synth.triggerRelease(notes);
        this.noiseOscillator.triggerRelease(Tone.Master.context.currentTime);
    }

    releaseAll() {
        this.synth.releaseAll();
        this.notes = [];
    }
    
    // sets the "amp envelopes" for the oscillators.
    // called when synth is triggered
    setEnvelopes(stepData) {
        this.carrierOscillator.setEnvelopes(stepData, this.ampEnvelope);
        this.setModulationEnvelopes();
        this.setAmpEnvelopeForNoise(stepData);
    }

    setModulationEnvelopes() {
        // set the attack and release of this trigger
        this.synth.set({
                "modulationEnvelope": {
                    "attack": new Tone.Time((1 + this.modulationEnvelope.attack) / 1000.0),
                    "decay": new Tone.Time((1 + this.modulationEnvelope.decay) / 1000.0),
                    "sustain": this.modulationEnvelope.sustain,
                    "release": new Tone.Time((1 + this.modulationEnvelope.release) / 1000.0),
                },
        });
    }

    setAmpEnvelopeForNoise() {
        this.noiseOscillator.envelope.attack = new Tone.Time((1 + this.ampEnvelope.attack) / 1000.0);
        this.noiseOscillator.envelope.decay = new Tone.Time((1 + this.ampEnvelope.decay) / 1000.0);
        this.noiseOscillator.envelope.sustain = this.ampEnvelope.sustain;
        this.noiseOscillator.envelope.release = new Tone.Time((1 + this.ampEnvelope.release) / 1000.0);
    }

    dispose() {
        this.noiseOscillator.dispose();
        this.filterEnvelope.dispose();
        this.filterEnvelope2.dispose();
        this.filter.dispose();
        this.carrierOscillator.dispose();

        this.noiseOscillator = undefined;
        this.filterEnvelope = undefined;
        this.filterEnvelope2 = undefined;
        this.filter = undefined;
        this.carrierOscillator = undefined;
    }

    connectSynthToFilter() {
        this.synth
            .connect(this.filter.effectNode);

        this.noiseOscillator
            .connect(this.filter.effectNode);
        
    }

    // connects the synth to the given audio node (i.e. the mix)
    connect(node) {
        if (node !== undefined) {
            this.filter.connect(node);
        }
    }

    // disconnects the synth from the given audio node (i.e. the mix)
    disconnect(node) {
        this.filter.disconnect(node);
    }

    setupParameters() {
        // Filter cut off - can be synced to LFO
        this.filterCutoffParameter = new AbstractAudioParamParameter(
            'Filter Cutoff',
            this.filter.effectNode.frequency, (o, x) => {
                this.filterEnvelope.frequency = x;
                this.filterEnvelope2.frequency = x;
                this.filter.cutoffFrequency = x;
                // this.oscillators.forEach(osc => osc.cutoffFrequency = x);
            },
            {
                min: 0,
                max: 10000,
                unit: 'HZ',
                defaultValue: 1200,
                constantAutomationRate: true,
            });

        this.registerParameter(
            this.filterCutoffParameter, (voice) => voice.filter.frequency);

        // Filter's Q - can be synced to LFO
        this.filterQParameter = new AbstractAudioParamParameter(
            'Filter Q',
            this.filter.effectNode.Q, (o, x) => this.filter.Q = x,
            {
                min: 0,
                max: 9,
                unit: '',
                defaultValue: 1
            });

        this.registerParameter(
            this.filterQParameter, (voice) => voice.filter.Q);

        this.noiseVolumeParameter = new AbstractAudioParamParameter(
            'noiseVolume',
            this.noiseOscillator.volume, (o, x) => this.noiseOscillator.volume.value = x,
            {
                min: 0,
                max: 1,
                unit: 'db',
                defaultValue: 0
            });

        this.registerParameter(
            this.noiseVolumeParameter);
   }

    registerParameter(parameter) {
        parameter.setStore(this.store);
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    getParameters() {
        return [
            ... this.parameters,
            ...this.ampEnvelope.parameters,
            ...this.filterEnvelope.parameters,
            ...this.filterEnvelope2.parameters,
            ...this.modulationEnvelope.parameters,
            ...this.carrierOscillator.parameters,
            ...this.modulatorOscillator.parameters,
            
        ];
                
    }

    // getters and setters for automatable synth parameters
    get filterCutoff() {
        return this.filterCutoffParameter.get();
    }

    set filterCutoff(cutoff) {
        this.filterCutoffParameter.set(cutoff);
    }

    get filterQ() {
        return this.filterQParameter.get();
    }

    set filterQ(q) {
        this.filterQParameter.set(q);
    }

    isMonophonic() {
        return this.carrierOscillator.polyphony === 1;
    }

    // refreshes the synth's parameters to their saved state
    refreshParameters() {
        this.filterQ = this.filterQ;
        this.filterCutoff = this.filterCutoff;
    }

    getName() {
        return 'FM Synth';
    }

    getInstrument() {
        return this;
    }

    getInstrumentType() {
        return FM_SYNTH;
    }

    getJson() {
        let json = {
            instrumentType: this.getInstrumentType(),
            filterCutoff: this.filterCutoff,
            filterQ: this.filterQ,
            carrierOscillator: this.carrierOscillator.getJson(),
            modulatorOscillator: this.modulatorOscillator.getJson(),
            ampEnvelope: this.ampEnvelope.getJson(),
            filterEnvelope: this.filterEnvelope.getJson(),
            filterEnvelope2: this.filterEnvelope2.getJson(),
            modulationEnvelope: this.modulationEnvelope.getJson(),
            noiseVolume: this.noiseVolumeParameter.get(),
        };

        json = saveParameterToJson(json, 'filterCutoffParameter', this.filterCutoffParameter);
        json = saveParameterToJson(json, 'filterQParameter', this.filterQParameter);
        json = saveParameterToJson(json, 'noiseVolumeParameter', this.noiseVolumeParameter);
        
        return json;
    }

    fromJson(json) {
        this.instrumentType = json.instrumentType;
        this.filterCutoff = json.filterCutoff;
        this.filterQ = json.filterQ;
        for (let i in json.lfos) {
            this.lfos[i].fromJson(json.lfos[i]);
        }

        this.carrierOscillator.fromJson(json.carrierOscillator);
        this.modulatorOscillator.fromJson(json.modulatorOscillator);

        this.filterEnvelope.fromJson(json.filterEnvelope);
        if (json.filterEnvelope2) {
            this.filterEnvelope2.fromJson(json.filterEnvelope2);
        }
        this.ampEnvelope.fromJson(json.ampEnvelope);
        this.modulationEnvelope.fromJson(json.modulationEnvelope);

        loadParameterFromJson(
           json, 'filterCutoffParameter', this.filterCutoffParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'filterQParameter', this.filterQParameter, this.sequencerEngine);
    }

    getCopy(context) {
        let instrument = new FMSynth(this.sequencerEngine, this.mixSession, context);
        instrument.fromJson(this.getJson());
        return instrument;
    }

    mateJson(partner, probability=0.5) {
        this.carrierOscillator.mateJson(partner.carrierOscillator, probability);
        this.modulatorOscillator.mateJson(partner.modulatorOscillator, probability);
        this.ampEnvelope.mateJson(partner.ampEnvelope, probability);
        this.filterEnvelope.mateJson(partner.filterEnvelope, probability);
        this.filterEnvelope2.mateJson(partner.filterEnvelope2, probability);
        this.modulationEnvelope.mateJson(partner.modulationEnvelope, probability);
        this.mateParameter('filterCutoff', partner, this.filterCutoffParameter, probability);
        this.mateParameter('filterQ', partner, this.filterQParameter, probability);
        this.mateParameter('noiseVolume', partner, this.noiseVolumeParameter, probability);
    }

    mateParameter(name, data, param1, probability) {
        mate(this.sequencerEngine, name, data, param1, probability);
    }

    findParameter(automationJson, name) {
        let envType = automationJson.envelopeType;
        let parameters = this.getParameters();
        if (envType === AMP) {
            parameters = this.ampEnvelope.parameters;
        } else if (envType === MODULATION) {
            parameters = this.modulationEnvelope.parameters;
        } else if (envType === FILTER) {
            parameters = this.filterEnvelope.parameters;
        } else if (envType === FILTER_2) {
            parameters = this.filterEnvelope2.parameters;
        }

        let found = parameters.filter(x => x.name === name);
        if (found.length === 0) {
            return undefined;
        }

        return found[0];
    }
}

export default FMSynth;
 
